/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState, useEffect } from 'react';
import { Bar, Radar, Line } from 'react-chartjs-2';
import axios from 'axios';
import BrandSelector from './BrandSelector';
import ExportMenu from './ExportMenu';
import { exportComparisonData } from '../utils/exportUtils';
import { useTheme } from '../contexts/ThemeContext';
import { isDemoMode } from '../services';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface BrandData {
  brand_id: string;
  name: string;
  metrics: any;
}

const BrandComparison: React.FC = () => {
  const { isDark } = useTheme();
  const [brands, setBrands] = useState<BrandData[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [comparisonData, setComparisonData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [competitiveAnalysis, setCompetitiveAnalysis] = useState<string>('');
  const [analysisLoading, setAnalysisLoading] = useState(false);

  useEffect(() => {
    fetchAllBrands();
  }, []);

  const fetchAllBrands = async () => {
    try {
      let brandsList: any[] = [];

      if (isDemoMode) {
        // Mock brands for demo mode
        brandsList = [
          { brand_id: 'coca-cola', name: 'Coca-Cola' },
          { brand_id: 'pepsi', name: 'Pepsi' },
          { brand_id: 'sprite', name: 'Sprite' },
          { brand_id: 'fanta', name: 'Fanta' },
          { brand_id: 'mountain-dew', name: 'Mountain Dew' }
        ];
      } else {
        const response = await axios.get('/api/v2/metrics/brands');
        brandsList = response.data.brands || [];
      }

      // Deduplicate brands by brand_id
      const uniqueBrands = brandsList.reduce((acc: any[], brand: any) => {
        if (!acc.find(b => b.brand_id === brand.brand_id)) {
          acc.push(brand);
        }
        return acc;
      }, []);
      setBrands(uniqueBrands);
      // Auto-select first 3 brands
      const firstThree = uniqueBrands.slice(0, 3).map((b: any) => b.brand_id);
      setSelectedBrands(firstThree);
      fetchComparisonData(firstThree);
    } catch (error) {
      console.error('Failed to fetch brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComparisonData = async (brandIds: string[]) => {
    try {
      let data: any[] = [];

      if (isDemoMode) {
        // Mock comparison data for demo mode
        const mockBrandData: Record<string, any> = {
          'coca-cola': {
            brand_id: 'coca-cola',
            name: 'Coca-Cola',
            respondent_count: 1250,
            metrics: {
              awareness: 0.92, consideration: 0.78, usage: 0.65, recent_usage: 0.58,
              primary_brand: 0.42, loyalty: 0.72, nps: 45,
              trust: 0.85, pride: 0.68, innovation: 0.62, value: 0.71,
              quality: 0.88, taste: 0.91, texture: 0.75, natural: 0.45
            }
          },
          'pepsi': {
            brand_id: 'pepsi',
            name: 'Pepsi',
            respondent_count: 1180,
            metrics: {
              awareness: 0.88, consideration: 0.72, usage: 0.58, recent_usage: 0.52,
              primary_brand: 0.35, loyalty: 0.65, nps: 38,
              trust: 0.78, pride: 0.62, innovation: 0.68, value: 0.75,
              quality: 0.82, taste: 0.85, texture: 0.72, natural: 0.42
            }
          },
          'sprite': {
            brand_id: 'sprite',
            name: 'Sprite',
            respondent_count: 980,
            metrics: {
              awareness: 0.82, consideration: 0.65, usage: 0.48, recent_usage: 0.42,
              primary_brand: 0.22, loyalty: 0.58, nps: 32,
              trust: 0.75, pride: 0.55, innovation: 0.52, value: 0.78,
              quality: 0.79, taste: 0.82, texture: 0.68, natural: 0.55
            }
          },
          'fanta': {
            brand_id: 'fanta',
            name: 'Fanta',
            respondent_count: 850,
            metrics: {
              awareness: 0.78, consideration: 0.58, usage: 0.42, recent_usage: 0.35,
              primary_brand: 0.18, loyalty: 0.52, nps: 28,
              trust: 0.72, pride: 0.58, innovation: 0.65, value: 0.72,
              quality: 0.75, taste: 0.78, texture: 0.65, natural: 0.48
            }
          },
          'mountain-dew': {
            brand_id: 'mountain-dew',
            name: 'Mountain Dew',
            respondent_count: 720,
            metrics: {
              awareness: 0.72, consideration: 0.52, usage: 0.38, recent_usage: 0.32,
              primary_brand: 0.15, loyalty: 0.48, nps: 25,
              trust: 0.68, pride: 0.52, innovation: 0.72, value: 0.68,
              quality: 0.72, taste: 0.75, texture: 0.62, natural: 0.38
            }
          }
        };
        data = brandIds.map(id => mockBrandData[id]).filter(Boolean);
      } else {
        const promises = brandIds.map(id => axios.get(`/api/v2/metrics/brands/${id}`));
        const responses = await Promise.all(promises);
        data = responses.map(r => r.data);
      }

      setComparisonData(data);

      // Fetch competitive analysis if we have brands to compare
      if (brandIds.length >= 2) {
        fetchCompetitiveAnalysis(brandIds);
      }
    } catch (error) {
      console.error('Failed to fetch comparison data:', error);
    }
  };

  const fetchCompetitiveAnalysis = async (brandIds: string[]) => {
    setAnalysisLoading(true);
    try {
      // Generate competitive analysis based on actual data
      if (comparisonData.length >= 2) {
        const brands = comparisonData.map(d => ({
          name: d.name,
          awareness: d.metrics?.awareness || 0,
          consideration: d.metrics?.consideration || 0,
          nps: d.metrics?.nps || 0,
          quality: d.metrics?.quality || 0,
          value: d.metrics?.value || 0
        }));
        
        // Sort brands by awareness
        brands.sort((a, b) => b.awareness - a.awareness);
        const leader = brands[0];
        const followers = brands.slice(1);
        
        // Build competitive insights
        const insights = [];
        
        // Market position
        insights.push(`${leader.name} leads with ${(leader.awareness * 100).toFixed(1)}% awareness, ${((leader.awareness - followers[0].awareness) * 100).toFixed(1)}pp ahead of nearest competitor.`);
        
        // Conversion efficiency
        const leaderConversion = leader.consideration / leader.awareness;
        const avgFollowerConversion = followers.reduce((sum, b) => sum + (b.consideration / b.awareness), 0) / followers.length;
        if (leaderConversion > avgFollowerConversion) {
          insights.push(`${leader.name} shows superior conversion efficiency at ${(leaderConversion * 100).toFixed(1)}% vs category average of ${(avgFollowerConversion * 100).toFixed(1)}%.`);
        }
        
        // Quality vs Value positioning
        const qualityLeader = brands.reduce((max, b) => b.quality > max.quality ? b : max);
        const valueLeader = brands.reduce((max, b) => b.value > max.value ? b : max);
        if (qualityLeader.name !== valueLeader.name) {
          insights.push(`${qualityLeader.name} dominates quality perception while ${valueLeader.name} wins on value, suggesting distinct positioning strategies.`);
        }
        
        // NPS comparison
        const npsLeader = brands.reduce((max, b) => b.nps > max.nps ? b : max);
        if (npsLeader.nps > 0) {
          insights.push(`${npsLeader.name} has strongest advocacy with NPS of ${npsLeader.nps}, indicating superior customer satisfaction.`);
        }
        
        // Growth opportunities
        const weakestAwareness = brands[brands.length - 1];
        if (weakestAwareness.consideration / weakestAwareness.awareness > leaderConversion) {
          insights.push(`${weakestAwareness.name} shows strong conversion despite low awareness, suggesting high growth potential with increased marketing.`);
        }
        
        setCompetitiveAnalysis(insights.join(' '));
      } else {
        setCompetitiveAnalysis('Select at least 2 brands to generate competitive analysis.');
      }
    } catch (error) {
      console.error('Failed to generate competitive analysis:', error);
      setCompetitiveAnalysis('Unable to generate competitive analysis at this time.');
    } finally {
      setAnalysisLoading(false);
    }
  };

  const toggleBrand = (brandId: string) => {
    let newSelection;
    if (selectedBrands.includes(brandId)) {
      newSelection = selectedBrands.filter(id => id !== brandId);
    } else if (selectedBrands.length < 5) {
      newSelection = [...selectedBrands, brandId];
    } else {
      return; // Max 5 brands
    }
    setSelectedBrands(newSelection);
    if (newSelection.length > 0) {
      fetchComparisonData(newSelection);
    }
  };

  // Brand Funnel Comparison
  const funnelComparisonData = {
    labels: ['Awareness', 'Consideration', 'Usage', 'Recent Usage', 'Primary Brand', 'Loyalty'],
    datasets: comparisonData.map((brand, index) => ({
      label: brand.name,
      data: [
        (brand.metrics?.awareness || 0) * 100,
        (brand.metrics?.consideration || 0) * 100,
        (brand.metrics?.usage || 0) * 100,
        (brand.metrics?.recent_usage || 0) * 100,
        (brand.metrics?.primary_brand || 0) * 100,
        (brand.metrics?.loyalty || 0) * 100
      ],
      backgroundColor: `hsla(${index * 60 + 200}, 70%, 50%, 0.6)`,
      borderColor: `hsla(${index * 60 + 200}, 70%, 50%, 1)`,
      borderWidth: 2
    }))
  };

  // Brand Attributes Comparison
  const attributesComparisonData = {
    labels: ['Trust', 'Pride', 'Innovation', 'Value', 'Quality', 'Taste', 'Texture', 'Natural'],
    datasets: comparisonData.map((brand, index) => ({
      label: brand.name,
      data: [
        (brand.metrics?.trust || 0) * 100,
        (brand.metrics?.pride || 0) * 100,
        (brand.metrics?.innovation || 0) * 100,
        (brand.metrics?.value || 0) * 100,
        (brand.metrics?.quality || 0) * 100,
        (brand.metrics?.taste || 0) * 100,
        (brand.metrics?.texture || 0) * 100,
        (brand.metrics?.natural || 0) * 100
      ],
      backgroundColor: `hsla(${index * 60 + 200}, 70%, 50%, 0.2)`,
      borderColor: `hsla(${index * 60 + 200}, 70%, 50%, 1)`,
      pointBackgroundColor: `hsla(${index * 60 + 200}, 70%, 50%, 1)`,
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: `hsla(${index * 60 + 200}, 70%, 50%, 1)`
    }))
  };

  // NPS Comparison
  const npsComparisonData = {
    labels: comparisonData.map(b => b.name),
    datasets: [{
      label: 'Net Promoter Score',
      data: comparisonData.map(b => b.metrics?.nps || 0),
      backgroundColor: comparisonData.map((_, i) => 
        `hsla(${i * 60 + 200}, 70%, 50%, 0.6)`
      ),
      borderColor: comparisonData.map((_, i) => 
        `hsla(${i * 60 + 200}, 70%, 50%, 1)`
      ),
      borderWidth: 2
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: isDark ? '#E5E7EB' : '#374151'
        }
      },
      tooltip: {
        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.95)',
        titleColor: isDark ? '#fff' : '#111827',
        bodyColor: isDark ? '#E5E7EB' : '#374151',
        borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        callbacks: {
          label: (context: any) => {
            return `${context.dataset.label}: ${context.parsed.y?.toFixed(1) || context.parsed}%`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'
        },
        ticks: {
          color: isDark ? '#9CA3AF' : '#4B5563'
        }
      },
      y: {
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'
        },
        ticks: {
          color: isDark ? '#9CA3AF' : '#4B5563',
          callback: (value: any) => `${value}%`
        }
      }
    }
  };

  const radarOptions = {
    ...chartOptions,
    scales: {
      r: {
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        },
        pointLabels: {
          color: isDark ? '#E5E7EB' : '#1F2937',
          font: {
            size: 11
          }
        },
        ticks: {
          color: isDark ? '#9CA3AF' : '#4B5563',
          backdropColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.9)',
          callback: (value: any) => `${value}%`
        },
        suggestedMin: 0,
        suggestedMax: 100
      }
    }
  };

  const npsOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        suggestedMin: -100,
        suggestedMax: 100,
        ticks: {
          color: isDark ? '#9CA3AF' : '#4B5563',
          callback: (value: any) => value
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Brand Selector */}
      <div className="bg-card rounded-2xl p-6 border border-border">
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-foreground mb-2">Select Brands to Compare</h2>
          <p className="text-text-muted">Choose up to 5 brands for side-by-side comparison</p>
        </div>
        <BrandSelector
          brands={brands}
          selectedBrands={selectedBrands}
          onBrandToggle={toggleBrand}
          multiSelect={true}
          maxSelection={5}
        />
      </div>

      {comparisonData.length > 0 && (
        <div id="comparison-dashboard">
          {/* AI Competitive Analysis */}
          {competitiveAnalysis && (
            <div className="bg-card rounded-xl p-4 border border-border bg-primary/5">
              <div className="flex items-start space-x-3">
                <i className="fas fa-chart-line text-primary mt-1"></i>
                <div className="flex-1">
                  <h3 className="text-primary font-medium mb-2">Competitive Intelligence</h3>
                  {analysisLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      <span className="text-text-muted text-sm">Analyzing competitive landscape...</span>
                    </div>
                  ) : (
                    <p className="text-text-secondary text-sm leading-relaxed">{competitiveAnalysis}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Key Metrics Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
            {comparisonData.map((brand, index) => (
              <div key={brand.brand_id} className="bg-card rounded-xl p-4 border border-border">
                <h3 className="text-lg font-semibold text-foreground mb-3">{brand.name}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-text-muted text-sm">Overall Score</span>
                    <span className="text-foreground font-semibold">{((brand.metrics?.loyalty || brand.metrics?.satisfaction || 0) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted text-sm">Awareness</span>
                    <span className="text-foreground">{((brand.metrics?.awareness || 0) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted text-sm">NPS</span>
                    <span className={`font-semibold ${
                      brand.metrics?.nps > 50 ? 'text-green-400' :
                      brand.metrics?.nps > 0 ? 'text-yellow-400' : 'text-red-400'
                    }`}>{brand.metrics?.nps || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted text-sm">Total Responses</span>
                    <span className="text-foreground">{(brand.respondent_count || brand.metrics?.total_count || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Brand Funnel Comparison */}
          <div className="bg-card rounded-2xl p-6 border border-border mt-4">
            <h3 className="text-xl font-semibold text-foreground mb-4">Brand Funnel Comparison</h3>
            <div className="h-80" id="brand-funnel-chart">
              <Bar data={funnelComparisonData} options={chartOptions} />
            </div>
          </div>

          {/* Brand Attributes Radar */}
          <div className="bg-card rounded-2xl p-6 border border-border mt-4">
            <h3 className="text-xl font-semibold text-foreground mb-4">Brand Attributes Comparison</h3>
            <div className="h-80">
              <Radar data={attributesComparisonData} options={radarOptions} />
            </div>
          </div>

          {/* NPS Comparison */}
          <div className="bg-card rounded-2xl p-6 border border-border mt-4">
            <h3 className="text-xl font-semibold text-foreground mb-4">Net Promoter Score Comparison</h3>
            <div className="h-64">
              <Bar data={npsComparisonData} options={npsOptions} />
            </div>
          </div>

          {/* Digital Metrics Comparison */}
          <div className="bg-card rounded-2xl p-6 border border-border mt-4">
            <h3 className="text-xl font-semibold text-foreground mb-4">
              <i className="fas fa-globe mr-2 text-primary"></i>
              Digital Presence Comparison
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-text-muted font-medium">Brand</th>
                    <th className="text-center py-3 px-4 text-text-muted font-medium">Social Followers</th>
                    <th className="text-center py-3 px-4 text-text-muted font-medium">Engagement Rate</th>
                    <th className="text-center py-3 px-4 text-text-muted font-medium">Share of Voice</th>
                    <th className="text-center py-3 px-4 text-text-muted font-medium">Sentiment</th>
                    <th className="text-center py-3 px-4 text-text-muted font-medium">Online Mentions</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((brand, idx) => {
                    // Generate mock digital metrics based on awareness
                    const awareness = brand.metrics?.awareness || 0.5;
                    const followers = Math.round(awareness * 500 + Math.random() * 200);
                    const engagementRate = (2 + awareness * 4 + Math.random() * 2).toFixed(1);
                    const shareOfVoice = Math.round(awareness * 40 + Math.random() * 15);
                    const sentiment = Math.round(60 + awareness * 30 + Math.random() * 10);
                    const mentions = Math.round(awareness * 10000 + Math.random() * 5000);

                    return (
                      <tr key={brand.brand_id} className="border-b border-border hover:bg-background-secondary">
                        <td className="py-3 px-4 text-foreground font-medium">{brand.name}</td>
                        <td className="text-center py-3 px-4 text-foreground">{followers}K</td>
                        <td className="text-center py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            parseFloat(engagementRate) > 4 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {engagementRate}%
                          </span>
                        </td>
                        <td className="text-center py-3 px-4 text-foreground">{shareOfVoice}%</td>
                        <td className="text-center py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            sentiment > 70 ? 'bg-green-500/20 text-green-400' :
                            sentiment > 50 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {sentiment}% Positive
                          </span>
                        </td>
                        <td className="text-center py-3 px-4 text-foreground">{(mentions / 1000).toFixed(1)}K</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Social Media Performance Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <div className="bg-card rounded-2xl p-6 border border-border">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                <i className="fas fa-chart-pie mr-2 text-primary"></i>
                Share of Voice Distribution
              </h3>
              <div className="space-y-3">
                {comparisonData.map((brand, idx) => {
                  const shareOfVoice = Math.round((brand.metrics?.awareness || 0.5) * 40 + 10);
                  const colors = ['bg-primary', 'bg-purple-500', 'bg-cyan-500', 'bg-pink-500', 'bg-orange-500'];
                  return (
                    <div key={brand.brand_id}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-foreground">{brand.name}</span>
                        <span className="text-text-muted">{shareOfVoice}%</span>
                      </div>
                      <div className="w-full bg-background-tertiary rounded-full h-2">
                        <div
                          className={`${colors[idx % colors.length]} h-2 rounded-full transition-all`}
                          style={{ width: `${shareOfVoice}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-card rounded-2xl p-6 border border-border">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                <i className="fas fa-heart mr-2 text-pink-500"></i>
                Engagement Performance
              </h3>
              <div className="space-y-3">
                {comparisonData.map((brand, idx) => {
                  const engagement = (2 + (brand.metrics?.awareness || 0.5) * 4).toFixed(1);
                  const colors = ['bg-primary', 'bg-purple-500', 'bg-cyan-500', 'bg-pink-500', 'bg-orange-500'];
                  return (
                    <div key={brand.brand_id}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-foreground">{brand.name}</span>
                        <span className="text-text-muted">{engagement}%</span>
                      </div>
                      <div className="w-full bg-background-tertiary rounded-full h-2">
                        <div
                          className={`${colors[idx % colors.length]} h-2 rounded-full transition-all`}
                          style={{ width: `${parseFloat(engagement) * 10}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Brand vs Competitor Search Interest */}
          <div className="bg-card rounded-2xl p-6 border border-border mt-4">
            <h3 className="text-xl font-semibold text-foreground mb-4">Brand vs Competitor Search Interest</h3>
            <div className="space-y-4">
              {comparisonData.map((brand, idx) => {
                const searchInterest = Math.round((brand.metrics?.awareness || 0.5) * 100);
                return (
                  <div key={brand.brand_id} className="flex items-center gap-4">
                    <span className="text-foreground font-medium w-32 truncate">{brand.name}</span>
                    <div className="flex-1 bg-background-tertiary rounded-full h-4 relative">
                      <div
                        className={`h-4 rounded-full ${idx === 0 ? 'bg-primary' : 'bg-gray-500'}`}
                        style={{ width: `${searchInterest}%` }}
                      />
                    </div>
                    <span className="text-foreground font-medium w-12 text-right">{searchInterest}%</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Equity by Brand */}
          <div className="bg-card rounded-2xl p-6 border border-border mt-4">
            <h3 className="text-xl font-semibold text-foreground mb-4">
              <i className="fas fa-chart-bar mr-2 text-primary"></i>
              Equity by Brand
            </h3>
            <div className="h-72">
              <Bar
                data={{
                  labels: comparisonData.map(brand => brand.name),
                  datasets: [
                    {
                      label: 'Traditional Equity',
                      data: comparisonData.map((brand, idx) => {
                        const baseEquity = [85.5, 76.8, 68.3, 62.7, 71.2];
                        return baseEquity[idx] || 70 + Math.random() * 15;
                      }),
                      backgroundColor: 'rgba(59, 130, 246, 0.7)',
                      borderColor: 'rgba(59, 130, 246, 1)',
                      borderWidth: 1
                    },
                    {
                      label: 'Digital Equity',
                      data: comparisonData.map((brand, idx) => {
                        const baseEquity = [78.2, 82.4, 71.5, 65.8, 76.9];
                        return baseEquity[idx] || 65 + Math.random() * 20;
                      }),
                      backgroundColor: 'rgba(16, 185, 129, 0.7)',
                      borderColor: 'rgba(16, 185, 129, 1)',
                      borderWidth: 1
                    },
                    {
                      label: 'Total Equity',
                      data: comparisonData.map((brand, idx) => {
                        const baseEquity = [81.9, 79.6, 69.9, 64.3, 74.1];
                        return baseEquity[idx] || 67 + Math.random() * 18;
                      }),
                      backgroundColor: 'rgba(147, 51, 234, 0.7)',
                      borderColor: 'rgba(147, 51, 234, 1)',
                      borderWidth: 1
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'top',
                      labels: {
                        color: isDark ? '#E5E7EB' : '#374151'
                      }
                    },
                    tooltip: {
                      callbacks: {
                        label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(1)}%`
                      }
                    }
                  },
                  scales: {
                    x: {
                      grid: {
                        color: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
                      },
                      ticks: {
                        color: isDark ? '#9CA3AF' : '#4B5563'
                      }
                    },
                    y: {
                      beginAtZero: true,
                      max: 100,
                      grid: {
                        color: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
                      },
                      ticks: {
                        color: isDark ? '#9CA3AF' : '#4B5563',
                        callback: (value) => `${value}%`
                      }
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* Competitive Positioning Matrix */}
          <div className="bg-card rounded-2xl p-6 border border-border mt-4">
            <h3 className="text-xl font-semibold text-foreground mb-4">Competitive Positioning</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-semibold text-text-muted uppercase mb-3">Strengths</h4>
                {comparisonData.map(brand => {
                  const strengths = [];
                  if ((brand.metrics?.awareness || 0) > 0.6) strengths.push('High Awareness');
                  if ((brand.metrics?.trust || 0) > 0.6) strengths.push('Strong Trust');
                  if ((brand.metrics?.quality || 0) > 0.6) strengths.push('Quality Leader');
                  if (brand.metrics?.nps > 30) strengths.push('High NPS');

                  return strengths.length > 0 ? (
                    <div key={brand.brand_id} className="mb-3">
                      <p className="text-foreground font-medium">{brand.name}</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {strengths.map((s, i) => (
                          <span key={i} className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-text-muted uppercase mb-3">Opportunities</h4>
                {comparisonData.map(brand => {
                  const opportunities = [];
                  if ((brand.metrics?.consideration || 0) < (brand.metrics?.awareness || 0) * 0.7)
                    opportunities.push('Improve Consideration');
                  if ((brand.metrics?.primary_brand || 0) < 0.3)
                    opportunities.push('Build Primary Usage');
                  if ((brand.metrics?.innovation || 0) < 0.5)
                    opportunities.push('Innovation Perception');

                  return opportunities.length > 0 ? (
                    <div key={brand.brand_id} className="mb-3">
                      <p className="text-foreground font-medium">{brand.name}</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {opportunities.map((o, i) => (
                          <span key={i} className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded">
                            {o}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandComparison;
